// What Design B's car panel and phone sheet show about a car (1NOW-11219): the spec cells, the
// description, the features and the photos, each cleaned of the junk hosts type into FMS.
import { cardPhotoUrl, mileageLabel, photoUrl } from "@1-now/booking-core";
import { carSpecValues, specNumber, specText } from "utils/carSpecs";
import type { IconName } from "designs/DesignB/components/icons";

export type DetailSpec = { key: string; icon: IconName; value: string; label: string };

const titleCase = (s: string) => s.replace(/[_-]+/g, " ").toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());

// A fuel that is not one ("fuel", "x") is dropped like any other placeholder.
const FUEL_WORDS = /^(gas|gasoline|petrol|diesel|electric|ev|hybrid|plug|phev|hev|hydrogen|flex|e85|lpg|cng|regular|premium|unleaded)/i;
const MAX_AGE = 99;

/** A colour that fits half a cell: two of them share one ("Red / Black"). */
const SHORT_COLOUR = 20;

/**
 * The spec cells, in this order and only the ones with a real value: at most ten (seats, doors,
 * transmission, fuel, mileage, driver age, colour, interior, make, year). Since the Design B car
 * page dropped its Vehicle Information rows, these are the only place a renter sees the make and
 * a colour of any length, so both colours are kept: together in one cell when both are short,
 * else one cell each.
 */
export const detailSpecs = (car: any, company?: any): DetailSpec[] => {
  const { seats, doors, transmission } = carSpecValues(car);
  const fuel = specText(car?.fuel_type);
  const miles = mileageLabel(car, company);
  const minAge = specNumber(car?.min_age);
  const maxAge = specNumber(car?.max_age);
  const color = specText(car?.color);
  const inside = specText(car?.interior_color);
  const make = specText(car?.make);
  const year = specNumber(car?.year);
  const out: DetailSpec[] = [];
  const add = (key: string, icon: IconName, value: string | null | false, label: string) => {
    if (value) out.push({ key, icon, value, label });
  };
  add("seats", "user", seats !== null && String(seats), "Seats");
  add("doors", "door", doors !== null && String(doors), "Doors");
  add("transmission", "gear", transmission, "Transmission");
  add("fuel", "fuel", fuel && FUEL_WORDS.test(fuel) && titleCase(fuel), "Fuel");
  add("mileage", "gauge", miles, "Mileage");
  const min = minAge !== null && minAge <= MAX_AGE ? Math.round(minAge) : null;
  const max = maxAge !== null && min !== null && maxAge > min && maxAge <= 120 ? Math.round(maxAge) : null;
  add("age", "id", min !== null && (max !== null ? `${min} to ${max}` : `${min}+`), "Driver age");
  const pair = !!color && !!inside && color.length <= SHORT_COLOUR && inside.length <= SHORT_COLOUR;
  add("color", "palette", color && (pair ? `${titleCase(color)} / ${titleCase(inside!)}` : titleCase(color)), pair ? "Outside / inside" : "Colour");
  add("interior", "palette", !pair && inside && titleCase(inside), "Interior");
  add("make", "car", make, "Make");
  const thisYear = new Date().getFullYear();
  add("year", "calendar", year !== null && year >= 1900 && year <= thisYear + 2 && String(Math.round(year)), "Year");
  return out;
};

/** Three times the longest real description: QA's 210,684-character test string stops here. */
export const DESCRIPTION_CAP = 3000;

/** The description as plain text with its line breaks kept, runs of blank lines folded, capped. */
export const descriptionText = (raw: unknown): string | null => {
  let s = specText(raw);
  if (!s) return null;
  s = s.replace(/\r\n?/g, "\n").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n");
  if (s.length > DESCRIPTION_CAP) {
    const cut = s.slice(0, DESCRIPTION_CAP);
    const space = cut.lastIndexOf(" ");
    s = (space > DESCRIPTION_CAP - 200 ? cut.slice(0, space) : cut).trimEnd() + "…";
  }
  return s;
};

/** Feature names, without blanks, placeholders or repeats. */
export const featureNames = (car: any): string[] =>
  Array.from(
    new Set(
      (Array.isArray(car?.features) ? car.features : [])
        .map((f: any) => specText(f && typeof f === "object" ? f.name : f))
        .filter(Boolean) as string[],
    ),
  );

/** Every photo, the one the card leads with first, each once. */
export const galleryPhotos = (car: any): string[] => {
  const all = [
    cardPhotoUrl(car),
    ...(Array.isArray(car?.fleet_photos) ? car.fleet_photos : []).map((p: any) => photoUrl(p?.photo_url)),
  ].filter(Boolean) as string[];
  return Array.from(new Set(all));
};
